import * as Yup from 'yup'
import { phoneHelpers } from '@/utils/phone'
import { RemarkInputType } from '~/view-models/remark'
// TODO: i18n
/**
 * 文件：
 * 使用時只需要在規則後面加上其他 yup function 即可
 * 例如: rule.email().required()
 * 或是用 concat 串接已經寫好的 schema，例如：
 * rule.onlyLetterAndNumber().concat(rule.wordLimit())
 * rule.required().concat(rule.email())
 * 預設所有選項都是必填，如果要選填在後面加上 notRequired() 即可，例如：
 * rule.required().notRequired()
 */
const REQUIRED_MESSAGE_KEY = 'empty_error'

const FUNNOW_PASSWORD_REGEX = /^(?=.*[a-zA-Z0-9]).{6,}$/
const INVALID_CHAR_REGEX = /^[^`~!@#$%^&*()_+={}[\]|\\:;"'<,>.?๐฿]*$/u
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
const CARDHOLDER_NAME_REGEX = /^[a-zA-Z\s]+$/ // 僅允許英文字母和空格

export const useFieldRules = () => {
  const { t } = useI18n()
  const requiredMessage = t(REQUIRED_MESSAGE_KEY)

  return {
    required: () => Yup.string().trim().required(requiredMessage),
    requiredArray: () => Yup.array().min(1, requiredMessage),
    requiredQuantity: () =>
      Yup.array().test('required quantity', requiredMessage, items => (items as any[]).reduce((acc, c) => acc + c?.value, 0) > 0),
    onlyLetterAndNumber: () => Yup.string().trim().matches(INVALID_CHAR_REGEX, t('name_characters_error')).required(requiredMessage),
    wordLimit: () =>
      Yup.string()
        .trim()
        .test('len', t('nickname_length_constraint_msg'), (value = '') => {
          // db 限制 128 bytes
          const wordsBytesLength = new TextEncoder().encode(value).length

          const adjustedLength = value.split('').reduce((length: number, char: string) => {
            const charCode = char.charCodeAt(0)
            if (
              (charCode >= 0x4e00 && charCode <= 0x9fff) || // 中文
              (charCode >= 0x3000 && charCode <= 0x30ff) || // 日文
              (charCode >= 0xff01 && charCode <= 0xff5e) // 全形英文、數字、符號
            ) {
              return length + 2
            } else {
              return length + 1
            }
          }, 0)

          return wordsBytesLength <= 128 && adjustedLength <= 32
        })
        .required(requiredMessage),
    email: () => Yup.string().required(requiredMessage).matches(EMAIL_REGEX, t('login_account_error')),
    phone: (errMsg: string) =>
      Yup.string()
        .trim()
        .test('phone', errMsg, value => {
          if (!value) return true
          const [callingCode, phoneNumber] = value.split(' ')
          if (!callingCode || !phoneNumber) return true
          return phoneHelpers.isValidMobileNumber(phoneNumber, callingCode)
        }),
    requiredPhone: () =>
      Yup.string().test('required phone', requiredMessage, value => {
        if (!value) return false
        const [callingCode, phoneNumber] = value.split(' ')
        if (!callingCode || !phoneNumber) return false
        return true
      }),
    password: () => Yup.string().matches(FUNNOW_PASSWORD_REGEX, t('pwd_remark')).required(requiredMessage),
    confirmPassword: ({ refKey } = { refKey: 'password' }) =>
      Yup.string()
        .oneOf([Yup.ref(refKey)], t('pwd_not_consist_error'))
        .required(t('pwd_refilled_hint')),
    cardholderName: (errMsg: string) => Yup.string().trim().required(requiredMessage).matches(CARDHOLDER_NAME_REGEX, errMsg),
  }
}

type Remark = { is_required: boolean; regex?: string; error_msg?: string }

export function useRemarkStrategy(remark: Remark) {
  const rules = useFieldRules()
  const { is_required, error_msg, regex } = remark

  const strategyMap: any = {
    [RemarkInputType.NationalPhoneNumber]: is_required
      ? rules.requiredPhone().concat(rules.phone(error_msg as string))
      : rules.phone(error_msg as string),
    [RemarkInputType.Checkbox]: is_required ? rules.requiredArray() : Yup.array(),
    [RemarkInputType.Quantity]: Yup.array(),
    [RemarkInputType.Tags]: is_required ? rules.requiredArray() : Yup.array(),
    default: regex ? rules.required().matches(new RegExp(regex), { message: error_msg }) : rules.required(),
  }

  function getRuleByType(type: RemarkInputType) {
    return strategyMap[type] || strategyMap.default
  }

  return {
    getRuleByType,
  }
}
